import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { ApiError, api, type Session } from './api.js';
import { isUsable } from './session.js';
import { Calendar } from './Calendar.js';
import { Review } from './Review.js';
import { Topics } from './Topics.js';
import { Studio } from './Studio.js';
import { Settings } from './Settings.js';

export function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [chargement, setChargement] = useState(true);

  const rafraichir = useCallback(async () => {
    try {
      setSession(await api.me());
    } catch {
      setSession({ authenticated: false });
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => {
    void rafraichir();
  }, [rafraichir]);

  if (chargement) {
    return (
      <main className="connexion">
        <div className="chargement-marque" aria-label="Chargement"><span>AD</span></div>
      </main>
    );
  }

  if (!isUsable(session)) return <Connexion onConnecte={setSession} />;

  return <Atelier session={session as Session} onDeconnecte={() => setSession({ authenticated: false })} />;
}

function Connexion({ onConnecte }: { onConnecte: (session: Session) => void }) {
  const [email, setEmail] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);

  async function soumettre(event: FormEvent) {
    event.preventDefault();
    setErreur(null);
    setEnvoi(true);
    try {
      onConnecte(await api.login(email, motDePasse));
    } catch (error) {
      // Le serveur garde volontairement le même message pour une adresse
      // inconnue et un mot de passe faux afin de ne révéler aucun compte.
      setErreur(error instanceof ApiError ? error.message : 'Connexion impossible.');
    } finally {
      setEnvoi(false);
    }
  }

  return (
    <main className="connexion">
      <section className="connexion-intro" aria-hidden="true">
        <span className="marque-carree">AD</span>
        <p className="surtitre">Votre communication, au même endroit</p>
        <h1>Social prépare vos publications.</h1>
        <p>Des sujets vérifiés, des visuels composés et une décision simple avant diffusion.</p>
      </section>
      <form className="carte connexion-carte" onSubmit={soumettre}>
        <p className="surtitre">social.hub-ad</p>
        <h2>Bon retour, Alan.</h2>
        <p className="sous-titre">Connectez-vous pour ouvrir Social.</p>
        {erreur ? <p className="erreur" role="alert">{erreur}</p> : null}

        <label htmlFor="email">Adresse e-mail</label>
        <input id="email" type="email" autoComplete="username" inputMode="email" required value={email} onChange={(event) => setEmail(event.target.value)} />

        <label htmlFor="motdepasse">Mot de passe</label>
        <input id="motdepasse" type="password" autoComplete="current-password" required value={motDePasse} onChange={(event) => setMotDePasse(event.target.value)} />

        <button type="submit" disabled={envoi}>{envoi ? 'Connexion…' : 'Entrer dans Social'}</button>
      </form>
    </main>
  );
}

type Onglet = 'calendrier' | 'validation' | 'sujets' | 'studio' | 'settings' | 'service';
type IconName = 'calendar' | 'check' | 'idea' | 'service' | 'studio' | 'media' | 'chart' | 'settings' | 'plus' | 'bolt' | 'logout';

const PAGES: Record<Onglet, { surtitre: string; titre: string }> = {
  calendrier: { surtitre: 'Diffusion', titre: 'Calendrier' },
  validation: { surtitre: 'Validation', titre: 'Posts à relire' },
  sujets: { surtitre: 'Inspiration', titre: 'Sujets & veille' },
  studio: { surtitre: 'Création', titre: 'Studio vidéo' },
  settings: { surtitre: 'Configuration', titre: 'Réglages' },
  service: { surtitre: 'Application', titre: 'État du service' },
};

const ICONS: Record<IconName, string> = {
  calendar: 'M6 2v3M18 2v3M3 9h18M5 4h14a2 2 0 0 1 2 2v14H3V6a2 2 0 0 1 2-2Z',
  check: 'M9 11l3 3L22 4M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11',
  idea: 'M9 18h6M10 22h4M8.5 14.5A7 7 0 1 1 15.5 14.5C14.6 15.2 14 16.4 14 18h-4c0-1.6-.6-2.8-1.5-3.5Z',
  service: 'M4 12a8 8 0 1 0 16 0M12 8v4l3 2',
  studio: 'M12 3a9 9 0 0 0 0 18h1.5a2 2 0 0 0 0-4H12a2 2 0 0 1 0-4h5a4 4 0 0 0 4-4c0-3.3-4-6-9-6ZM7.5 10h.01M9.5 6.5h.01M14.5 6.5h.01',
  media: 'M4 5h16v14H4zM4 16l4-4 3 3 3-4 6 6M9 9h.01',
  chart: 'M4 20V10M10 20V4M16 20v-7M22 20H2',
  settings: 'M4 7h10M18 7h2M4 17h2M10 17h10M14 4v6M6 14v6',
  plus: 'M12 5v14M5 12h14',
  bolt: 'm13 2-9 12h7l-1 8 9-12h-7z',
  logout: 'M10 17l5-5-5-5M15 12H3M13 3h6a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-6',
};

function Icon({ name }: { name: IconName }) {
  return <svg className="icone" viewBox="0 0 24 24" aria-hidden="true"><path d={ICONS[name]} /></svg>;
}

function Atelier({ session, onDeconnecte }: { session: Session; onDeconnecte: () => void }) {
  const [onglet, setOnglet] = useState<Onglet>('calendrier');
  const [compteurs, setCompteurs] = useState({ sujets: 0, validation: 0 });

  const rafraichirCompteurs = useCallback(async () => {
    try {
      const [{ topics }, { posts }] = await Promise.all([api.topics('ready'), api.reviewQueue()]);
      setCompteurs({ sujets: topics.length, validation: posts.length });
    } catch {
      // Les écrans concernés afficheront leur erreur détaillée ; la navigation
      // reste utilisable même si un compteur ponctuel ne répond pas.
    }
  }, []);

  useEffect(() => {
    void rafraichirCompteurs();
  }, [onglet, rafraichirCompteurs]);

  function ouvrirNouveauPost() {
    setOnglet('calendrier');
    window.setTimeout(() => document.getElementById('nouveau-sujet')?.scrollIntoView({ behavior: 'smooth' }), 60);
  }

  const navigation: Array<{ id: Onglet; label: string; icon: IconName; badge?: number }> = [
    { id: 'calendrier', label: 'Calendrier', icon: 'calendar' },
    { id: 'validation', label: 'À valider', icon: 'check', badge: compteurs.validation },
    { id: 'sujets', label: 'Sujets & veille', icon: 'idea', badge: compteurs.sujets },
    { id: 'studio', label: 'Studio', icon: 'studio' },
    { id: 'settings', label: 'Réglages', icon: 'settings' },
    { id: 'service', label: 'Service', icon: 'service' },
  ];

  return (
    <div className="coque-app">
      <aside className="navigation-laterale">
        <div className="marque">
          <span className="marque-carree">AD</span>
          <span><strong>Social</strong><small>Alan Dereux · iad</small></span>
        </div>
        <nav aria-label="Navigation principale">
          {navigation.map((item) => (
            <NavButton key={item.id} actif={onglet === item.id} onClick={() => setOnglet(item.id)} icon={item.icon} badge={item.badge}>{item.label}</NavButton>
          ))}
          <div className="nav-separateur"><span>Prochaines étapes</span></div>
          <NavButton disabled icon="media">Médias</NavButton>
          <NavButton disabled icon="chart">Résultats</NavButton>
        </nav>
        <div className="profil-lateral">
          <span className="avatar">AD</span>
          <span><strong>{session.displayName ?? 'Alan Dereux'}</strong><small>Administrateur</small></span>
          <button type="button" title="Se déconnecter" onClick={() => void api.logout(session.csrfToken).then(onDeconnecte)}><Icon name="logout" /></button>
        </div>
      </aside>

      <div className="surface-app">
        <header className="entete-app">
          <div><span className="surtitre">{PAGES[onglet].surtitre}</span><h1>{PAGES[onglet].titre}</h1></div>
          <div className="actions-entete">
            <button type="button" className="bouton-propositions" onClick={() => setOnglet('sujets')}><Icon name="bolt" /> {compteurs.sujets} proposition{compteurs.sujets > 1 ? 's' : ''}</button>
            <button type="button" onClick={ouvrirNouveauPost}><Icon name="plus" /> Nouveau post</button>
          </div>
        </header>

        <main className="contenu-app">
          {onglet === 'calendrier' ? <Calendar csrfToken={session.csrfToken} /> : null}
          {onglet === 'validation' ? <Review csrfToken={session.csrfToken} /> : null}
          {onglet === 'sujets' ? <Topics csrfToken={session.csrfToken} /> : null}
          {onglet === 'studio' ? <Studio csrfToken={session.csrfToken} /> : null}
          {onglet === 'settings' ? <Settings csrfToken={session.csrfToken} /> : null}
          {onglet === 'service' ? <Apercu session={session} /> : null}
        </main>
      </div>

      <nav className="navigation-mobile" aria-label="Navigation mobile">
        {navigation.map((item) => (
          <NavButton key={item.id} actif={onglet === item.id} onClick={() => setOnglet(item.id)} icon={item.icon} badge={item.badge}>
            {item.id === 'validation' ? 'À valider' : item.id === 'sujets' ? 'Sujets' : item.label}
          </NavButton>
        ))}
      </nav>
    </div>
  );
}

function NavButton({ actif = false, disabled = false, onClick, icon, badge, children }: { actif?: boolean; disabled?: boolean; onClick?: () => void; icon: IconName; badge?: number | undefined; children: ReactNode }) {
  return (
    <button type="button" className={actif ? 'actif' : ''} disabled={disabled} onClick={onClick} title={disabled ? 'Prévu dans une prochaine étape' : undefined}>
      <Icon name={icon} /><span>{children}</span>{badge ? <b>{badge}</b> : null}
    </button>
  );
}

function Apercu({ session }: { session: Session }) {
  const [sante, setSante] = useState<{ status: string; commit: string } | null>(null);

  useEffect(() => {
    void api.health().then(setSante).catch(() => setSante(null));
  }, []);

  const sessionJusqua = session.expiresAt
    ? new Date(session.expiresAt).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })
    : '—';

  return (
    <section className="bloc service-page">
      <div className="intro-page">
        <div><p className="surtitre">Diagnostic</p><h2>Social est-il prêt à publier ?</h2></div>
        <p>Les informations techniques utiles, sans exposer les détails du serveur.</p>
      </div>
      <div className="grille-service">
        <article><span className={`voyant ${sante?.status === 'ok' ? 'ok' : 'ko'}`} /><small>Santé</small><strong>{sante?.status === 'ok' ? 'Opérationnel' : 'Indisponible'}</strong></article>
        <article><small>Version déployée</small><strong>{sante?.commit ?? '—'}</strong></article>
        <article><small>Session valable jusqu’au</small><strong>{sessionJusqua}</strong></article>
      </div>
    </section>
  );
}
